// EXIF metadata injection using piexifjs.
// Bakes Session Name + Base Tags into ImageDescription, XPComment and UserComment.

import piexif from "piexifjs";

const toBytesUcs2 = (str) => {
  // UCS-2 little-endian byte array for XPComment, terminated by null bytes.
  const out = [];
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    out.push(c & 0xff, (c >> 8) & 0xff);
  }
  out.push(0, 0);
  return out;
};

const userCommentBytes = (str) => {
  // EXIF UserComment (UNDEFINED type): piexifjs wants a binary string — 8-byte charset header + ASCII body.
  let body = "";
  for (let i = 0; i < str.length; i++) body += String.fromCharCode(str.charCodeAt(i) & 0xff);
  return "ASCII\0\0\0" + body;
};

/**
 * Inject EXIF metadata into a JPEG data URL.
 * @param {string} jpegDataUrl - "data:image/jpeg;base64,..."
 * @param {{sessionName: string, tags: string, sequence: number, filename: string}} meta
 * @returns {string} new data URL with EXIF
 */
export const injectExif = (jpegDataUrl, meta, opts = {}) => {
  try {
    const description = `${meta.sessionName} | ${meta.tags} | #${meta.sequence}`;
    const userComment = JSON.stringify({
      app: "AIImageSuites",
      session: meta.sessionName,
      tags: meta.tags,
      sequence: meta.sequence,
      filename: meta.filename,
      captured_at: meta.capturedAt || new Date().toISOString(),
      source: meta.source || "capture",
    });

    // preserve=true (SD-card / drone imports): start from the file's own EXIF so
    // camera make/model, original timestamps and GPS survive; we only overlay ours.
    let existing = null;
    if (opts.preserve) {
      try { existing = piexif.load(jpegDataUrl); } catch { existing = null; }
    }
    const now = new Date();
    const exifDateTime = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
    ].join(":") +
      " " +
      [
        String(now.getHours()).padStart(2, "0"),
        String(now.getMinutes()).padStart(2, "0"),
        String(now.getSeconds()).padStart(2, "0"),
      ].join(":");

    const zeroth = existing?.["0th"] ? { ...existing["0th"] } : {};
    const exif = existing?.Exif ? { ...existing.Exif } : {};
    zeroth[piexif.ImageIFD.ImageDescription] = description;
    zeroth[piexif.ImageIFD.Software] = meta.software || "AIImageSuite.pro / Session Capture";
    zeroth[piexif.ImageIFD.Artist] = meta.sessionName || "Session Capture";
    zeroth[piexif.ImageIFD.XPComment] = toBytesUcs2(userComment);
    zeroth[piexif.ImageIFD.XPKeywords] = toBytesUcs2(meta.tags || "");
    zeroth[piexif.ImageIFD.XPTitle] = toBytesUcs2(meta.filename || "");
    if (meta.ai) {
      const ascii = (s) => (s || "").replace(/[^\x20-\x7E]/g, "");
      if (meta.ai.alt_text) zeroth[piexif.ImageIFD.ImageDescription] = ascii(meta.ai.alt_text);
      if (meta.ai.title) zeroth[piexif.ImageIFD.XPTitle] = toBytesUcs2(meta.ai.title);
      if (meta.ai.alt_text) zeroth[piexif.ImageIFD.XPSubject] = toBytesUcs2(meta.ai.alt_text);
      zeroth[piexif.ImageIFD.XPKeywords] = toBytesUcs2([meta.tags, ...(meta.ai.tags || [])].filter(Boolean).join(";"));
    }
    if (!existing) zeroth[piexif.ImageIFD.DateTime] = exifDateTime;

    if (!exif[piexif.ExifIFD.DateTimeOriginal]) exif[piexif.ExifIFD.DateTimeOriginal] = exifDateTime;
    if (!exif[piexif.ExifIFD.DateTimeDigitized]) exif[piexif.ExifIFD.DateTimeDigitized] = exifDateTime;
    exif[piexif.ExifIFD.UserComment] = userCommentBytes(userComment);

    const exifObj = existing
      ? { ...existing, "0th": zeroth, Exif: exif, thumbnail: existing.thumbnail || null }
      : { "0th": zeroth, Exif: exif };
    const exifBytes = piexif.dump(exifObj);
    return piexif.insert(exifBytes, existing ? piexif.remove(jpegDataUrl) : jpegDataUrl);
  } catch (err) {
    // If EXIF injection fails for any reason, return the original image so capture
    // never breaks for the end user.
    console.warn("[exif] injection failed:", err);
    return jpegDataUrl;
  }
};

/**
 * Read GPS + original capture time from a JPEG data URL (drone / SD-card imports).
 * Returns { gps: {lat, lon, alt?, bearing?} | null, capturedAt: ISO | null, make, model }.
 */
export const readGpsAndTime = (jpegDataUrl) => {
  try {
    const data = piexif.load(jpegDataUrl);
    const g = data.GPS || {};
    const toDeg = (dms, ref) => {
      if (!dms || dms.length < 3) return null;
      const [d, m, s] = dms.map(([n, den]) => (den ? n / den : 0));
      const v = d + m / 60 + s / 3600;
      return ref === "S" || ref === "W" ? -v : v;
    };
    const rat = (r) => (r && r[1] ? r[0] / r[1] : null);
    const lat = toDeg(g[piexif.GPSIFD.GPSLatitude], g[piexif.GPSIFD.GPSLatitudeRef]);
    const lon = toDeg(g[piexif.GPSIFD.GPSLongitude], g[piexif.GPSIFD.GPSLongitudeRef]);
    let gps = null;
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      const altRaw = rat(g[piexif.GPSIFD.GPSAltitude]);
      const alt = altRaw == null ? null : g[piexif.GPSIFD.GPSAltitudeRef] === 1 ? -altRaw : altRaw;
      gps = { lat, lon, alt, bearing: rat(g[piexif.GPSIFD.GPSImgDirection]) };
    }
    const raw = data.Exif?.[piexif.ExifIFD.DateTimeOriginal] || data["0th"]?.[piexif.ImageIFD.DateTime];
    let capturedAt = null;
    if (raw && /^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
      const [d, t] = raw.split(" ");
      // EXIF wall-clock has no zone; treat as UTC to match the backend SRT/CSV parser.
      capturedAt = new Date(`${d.replace(/:/g, "-")}T${t}Z`).toISOString();
    }
    return { gps, capturedAt, make: data["0th"]?.[piexif.ImageIFD.Make] || null, model: data["0th"]?.[piexif.ImageIFD.Model] || null };
  } catch {
    return { gps: null, capturedAt: null, make: null, model: null };
  }
};

/**
 * Read EXIF back from a data URL — used for the gallery detail view.
 */
export const readExifSummary = (jpegDataUrl) => {
  try {
    const data = piexif.load(jpegDataUrl);
    const desc = data["0th"]?.[piexif.ImageIFD.ImageDescription];
    const dt = data["Exif"]?.[piexif.ExifIFD.DateTimeOriginal];
    return { description: desc || null, captured_at: dt || null };
  } catch {
    return { description: null, captured_at: null };
  }
};

/** Convert a data URL to a Blob (for ZIP export). */
export const dataUrlToBlob = (dataUrl) => {
  const [header, body] = dataUrl.split(",");
  const mime = /data:(.*?);base64/.exec(header)?.[1] || "image/jpeg";
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};
